/**
 * NotificationBell — alert notification inbox icon + dropdown.
 *
 * Shows a badge with the unread count (capped at 99+).
 * The dropdown lists the 50 most-recent notifications in reverse chronological
 * order with per-item mark-as-read, mark-all-read, and clear-all actions.
 * Closes on click-outside.
 *
 * Notification types: `"alert"` | `"system"` | `"info"` | generic.
 */
import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  markNotificationRead,
  markAllNotificationsRead,
  clearAllNotifications,
  Notification,
} from "../../store/alert-slice";
import "./NotificationBell.scss";

interface NotificationBellProps {
  colorTheme: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ colorTheme }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellBtnRef = useRef<HTMLButtonElement>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const notifications = useSelector(
    (state: any) => (state.alerts?.notifications || []) as Notification[]
  );
  const unreadCount = useSelector((state: any) => state.alerts?.unreadCount || 0);

  // Show most recent first
  const sortedNotifications = [...notifications].reverse().slice(0, 50);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(t) &&
        bellBtnRef.current && !bellBtnRef.current.contains(t)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    const rect = bellBtnRef.current?.getBoundingClientRect() ?? null;
    setAnchorRect(rect);
    setIsOpen(prev => !prev);
  }, []);

  const handleMarkRead = useCallback((id: string) => {
    dispatch(markNotificationRead(id));
  }, [dispatch]);

  const handleMarkAllRead = useCallback(() => {
    dispatch(markAllNotificationsRead());
  }, [dispatch]);

  const handleClearAll = useCallback(() => {
    dispatch(clearAllNotifications());
    setIsOpen(false);
  }, [dispatch]);

  const formatTime = (ts: number) => {
    const now = Date.now();
    const diff = now - ts;
    const time = new Date(ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    if (diff < 86400000) return time;
    const date = new Date(ts).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    return `${date}, ${time}`;
  };

  const getNotifIcon = (type: Notification["type"]) => {
    switch (type) {
      case "alert": return "🔔";
      case "system": return "⚙";
      case "info": return "ℹ";
      default: return "📢";
    }
  };

  return (
    <div className={`notification-bell-wrapper ${colorTheme}`}>
      <button
        ref={bellBtnRef}
        className={`notification-bell-btn ${isOpen ? "active" : ""}`}
        onClick={handleToggle}
        title={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
      >
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.6">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && anchorRect && createPortal(
        <div ref={dropdownRef} className="notification-dropdown" style={{
          position: "fixed",
          top: anchorRect.bottom + 2,
          right: window.innerWidth - anchorRect.right,
          left: "auto",
          zIndex: 99999,
        }}>
          <div className="notification-header">
            <span className="notification-title">Notifications</span>
            <div className="notification-actions">
              {unreadCount > 0 && (
                <button className="notif-action-btn" onClick={handleMarkAllRead}>
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button className="notif-action-btn danger" onClick={handleClearAll}>
                  Clear all
                </button>
              )}
            </div>
          </div>

          <div className="notification-list">
            {sortedNotifications.length === 0 ? (
              <div className="notification-empty">
                <span className="empty-icon">🔕</span>
                <span>No notifications</span>
              </div>
            ) : (
              sortedNotifications.map(notif => (
                <div
                  key={notif.id}
                  className={`notification-item ${notif.read ? "read" : "unread"}`}
                  onClick={() => !notif.read && handleMarkRead(notif.id)}
                >
                  <span className="notif-icon">{getNotifIcon(notif.type)}</span>
                  <div className="notif-content">
                    <div className="notif-title-text">{notif.title}</div>
                    <div className="notif-message">{notif.message}</div>
                    <div className="notif-time">{formatTime(notif.timestamp)}</div>
                  </div>
                  {!notif.read && <span className="notif-unread-dot" />}
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
